import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import {
  MENU_TRAINING_CHECKLIST_PATH,
  MENU_TRAINING_PILLAR_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";

const startFreeHref = "https://app.speakyourmenu.com/signup";
const canonicalUrl = `https://speakyourmenu.com${MENU_TRAINING_CHECKLIST_PATH}`;
const pillarUrl = `https://speakyourmenu.com${MENU_TRAINING_PILLAR_PATH}`;
const pageTitle = "Menu Training Checklist for Restaurants | Speak Your Menu";
const pageDescription = "A practical menu training checklist for restaurant operators who need onboarding, reinforcement, and service standards to stay consistent across shifts.";

const checklistSections = [
  {
    title: "Set the source of truth",
    items: [
      "Confirm dish descriptions, ingredients, modifiers, and allergens are current.",
      "Define the guest-facing language staff should use for key dishes and categories.",
      "Decide which menu sections new hires should learn first.",
    ],
  },
  {
    title: "Build the practice loop",
    items: [
      "Break training into short sessions staff can complete around shifts.",
      "Reinforce learning beyond orientation instead of relying on one-time memorization.",
      "Include realistic recall prompts so staff practice how they will answer guests.",
    ],
  },
  {
    title: "Protect service consistency",
    items: [
      "Review allergens, substitutions, and frequently misunderstood items every week.",
      "Use pre-shift time to reinforce weak spots, not reteach the full menu from scratch.",
      "Check whether different shifts are giving the same answers about the same dishes.",
    ],
  },
  {
    title: "Give managers visibility",
    items: [
      "Track who completed training and where knowledge gaps are clustering.",
      "Coach employees before hesitation or incorrect answers reach the floor.",
      "Repeat the cycle when the menu changes or a new service period launches.",
    ],
  },
];

export default function MenuTrainingChecklistPage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Restaurant Menu Training Program", item: pillarUrl },
      { "@type": "ListItem", position: 3, name: "Menu Training Checklist", item: canonicalUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="homepage-redesign resource-page-shell">
        <section className="homepage-section homepage-hero-section resource-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell">
            <div className="resource-hero-copy">
              <span className="homepage-label-chip">Checklist</span>
              <h1 className="homepage-hero-title resource-hero-title">A restaurant menu training checklist your managers can actually use.</h1>
              <p className="homepage-hero-subhead resource-hero-subhead">
                Use this checklist to make menu training more consistent across onboarding, pre-shift reinforcement, and day-to-day service standards.
              </p>
              <div className="homepage-hero-actions resource-hero-actions">
                <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
                <Link to={MENU_TRAINING_PILLAR_PATH} className="resource-secondary-link">Read the full guide</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Use it weekly</span>
            <h2 className="homepage-section-title resource-section-title">Menu training checklist</h2>
            <div className="resource-checklist-stack">
              {checklistSections.map((section) => (
                <article key={section.title} className="homepage-surface-card resource-card">
                  <h3 className="homepage-card-title">{section.title}</h3>
                  <ul className="resource-bullet-list">
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card">
              <span className="homepage-label-chip">Why it matters</span>
              <h2 className="homepage-section-title resource-section-title">Checklist discipline is what keeps service quality from drifting.</h2>
              <p className="homepage-section-body resource-section-body">
                Restaurants usually know what good menu training should look like. The harder part is protecting that standard when new hires arrive fast, the menu changes, and managers are buried in daily operations. A checklist gives the team an execution baseline.
              </p>
            </article>
            <article className="homepage-surface-card resource-card resource-card-accent">
              <span className="homepage-label-chip">Go deeper</span>
              <h2 className="homepage-card-title resource-aside-title">Need the full system behind the checklist?</h2>
              <p className="homepage-card-copy">The full guide explains why these steps matter and how to turn them into a repeatable menu training program.</p>
              <Link to={MENU_TRAINING_PILLAR_PATH} className="homepage-primary-cta resource-inline-cta">Read the guide</Link>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}